import type { ItemFieldRow } from './item-field-reader';
import { phpJsonString } from './php-json-values';
import { appendPhpCompileArray, StatusArrayWriteError } from './status-row-construction';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (row: unknown,key: string): unknown => arrayLike(row) && Object.prototype.propertyIsEnumerable.call(row,key)
    ? (row as Readonly<Record<string,unknown>>)[key] : undefined;
const upper = (value: string): string => value.replace(/[a-z]/g,char=>char.toUpperCase());
const scalarString = (value: unknown): string => {
    if (value == null || arrayLike(value)) throw new TypeError('Encounter link gate ID requires a scalar string');
    return phpJsonString(value);
};
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key],i)=>key===String(i)) ? entries.map(([,child])=>clone(child))
        : Object.fromEntries(entries.map(([key,child])=>[key,clone(child)]));
}
/** Missing compilation inputs are not simulated SQL failures. */
export class EncounterLinkInputUnavailable extends Error {
    constructor(public readonly dependency: string,public readonly id: string) { super('Encounter link input unavailable'); }
}
export interface EncounterLinkGateDependencies {
    queryExists: (id: string) => unknown;
    queryPrefill: (ids: readonly string[]) => unknown;
    queryFailure?: (kind: 'exists' | 'prefill') => void;
    invalidIteration?: () => void;
}
export interface EncounterLinkGate {
    exists: (id: string) => boolean;
    prefill: (ids: ItemFieldRow) => void;
    forget: (ids: ItemFieldRow) => void;
}
/** Isolated request-local state; no DB access or runtime invalidation hooks. */
export function createEncounterLinkGate(deps: EncounterLinkGateDependencies): EncounterLinkGate {
    const memo = new Map<string,boolean>(),covered = new Set<string>(),has = new Set<string>();
    let failed = false;
    return {
        exists: raw => {
            const id = scalarString(raw);
            if (failed) return true;
            const key = upper(id);
            if (memo.has(key)) return memo.get(key)!;
            if (covered.has(key)) { const result = has.has(key); memo.set(key,result); return result; }
            try { const result = deps.queryExists(id) !== false; memo.set(key,result); return result; }
            catch (error) {
                if (error instanceof EncounterLinkInputUnavailable) throw error;
                failed = true; deps.queryFailure?.('exists'); return true;
            }
        },
        prefill: ids => {
            if (!arrayLike(ids)) throw new TypeError('Encounter link prefill requires a PHP array');
            if (failed) return;
            const want = new Map<string,string>();
            for (const id of Object.values(ids)) {
                if (typeof id !== 'string' || id === '') continue;
                const key = upper(id);
                if (!memo.has(key) && !covered.has(key) && !want.has(key)) want.set(key,id);
            }
            if (!want.size) return;
            try {
                const rows = deps.queryPrefill([...want.values()]),found = new Set<string>();
                if (!arrayLike(rows)) deps.invalidIteration?.();
                else for (const id of Object.values(rows)) found.add(upper(phpJsonString(id)));
                for (const key of want.keys()) { covered.add(key); if (found.has(key)) has.add(key); }
            } catch (error) {
                if (error instanceof EncounterLinkInputUnavailable) throw error;
                deps.queryFailure?.('prefill');
            }
        },
        forget: ids => {
            if (!arrayLike(ids)) throw new TypeError('Encounter link forget requires a PHP array');
            for (const id of Object.values(ids)) if (typeof id === 'string' && id !== '') {
                const key = upper(id); memo.delete(key); covered.delete(key); has.delete(key);
            }
        },
    };
}
export interface InventoryEncounterLinkReaders {
    exists: (id: string) => boolean;
    fetchLinks: (filters: Readonly<Record<string,unknown>>) => unknown;
    missingRoot?: () => void;
    invalidIteration?: () => void;
}
export function compileInventoryEncounterLinks(input: ItemFieldRow,readers: InventoryEncounterLinkReaders): ItemFieldRow {
    if (!arrayLike(input)) throw new TypeError('Inventory encounter links require a PHP array');
    const readId = (): unknown => {
        if (!Object.prototype.propertyIsEnumerable.call(input,'inventory_id')) readers.missingRoot?.();
        return own(input,'inventory_id') ?? null;
    };
    if (!readers.exists(phpJsonString(readId()))) return clone(input) as ItemFieldRow;
    const rows = readers.fetchLinks({encounter_link_deleted:0,encounter_link_from:readId()});
    const out: Record<string,unknown> = Object.fromEntries(Object.entries(input));
    if (!arrayLike(rows)) readers.invalidIteration?.();
    else for (const entry of Object.values(rows)) {
        const slot = own(out,'inventory_encounter_link');
        if (slot != null && slot !== false && !arrayLike(slot)) throw new StatusArrayWriteError('Cannot append encounter link to a PHP scalar');
        out.inventory_encounter_link = appendPhpCompileArray(arrayLike(slot)?slot:[],entry);
    }
    return clone(out) as ItemFieldRow;
}
const columns = ['encounter_link_private','encounter_link_deleted','encounter_link_created_by_id','encounter_link_updated_by_id',
    'encounter_link_id','encounter_link_from','encounter_link_to','encounter_link_notes'] as const;
function scalarType(value: unknown): string {
    if (value == null) return 'null';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    const token = JSON.stringify(value);
    if (/^-?\d+$/.test(token)) {
        const integer = BigInt(token);
        if (integer >= -9223372036854775808n && integer <= 9223372036854775807n) return 'int';
    }
    return 'float';
}
/** Actual pure table-row projection, independent of the fetching SQL. */
export function compileEncounterLinkRow(input: unknown,warning?: (kind: 'missing' | 'invalid-offset-read',detail: string) => void): ItemFieldRow {
    return Object.fromEntries(columns.map(key=>{
        if (typeof input === 'string') throw new TypeError('Cannot access string offset with a column name');
        if (!arrayLike(input)) warning?.('invalid-offset-read',scalarType(input));
        else if (!Object.prototype.propertyIsEnumerable.call(input,key)) warning?.('missing',key);
        return [key,clone(own(input,key) ?? null)];
    }));
}
